import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { Avatar, Dropdown, Menu, Badge, Button, Drawer, Select, message } from 'antd'
import {
  BellOutlined,
  DownOutlined,
  MenuOutlined,
  HomeOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  DashboardOutlined,
  LogoutOutlined,
  UserAddOutlined,
  ShopOutlined,
  WalletOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  GoogleOutlined,
  NotificationOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  LockOutlined
,
  ScheduleOutlined
} from '@ant-design/icons'
import { RiCoupon3Line, RiNotificationBadgeFill } from 'react-icons/ri'
import { FaMoneyBillWave } from 'react-icons/fa'
import { TbCalendarCancel } from 'react-icons/tb'
import { MessageOutlined, StarOutlined } from '@ant-design/icons'
import {
  hasAnyUnreadAnnouncement
} from '../data/announcements'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { PHOTO_URL } from '../utils/apiutils'
import { useSocketMessage } from '../hooks/useSocket'
import api from '../utils/api'

/** 待處理訂單紅點，與 Sidebar 同一套樣式 */
const ShopBadge = styled.span`
  display: inline-block;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 8px;
  border-radius: 9px;
  background: #ff4d4f;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  vertical-align: middle;
`

const NavBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #fff;
  /* 響應式 margin：跟 App.js ContentWrapper 對齊 */
  /* 大桌機 (>1280): 60px 寬鬆 margin */
  margin: 16px 60px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  /* 中等寬度（平板橫向 / 小筆電）：margin 縮小到 16px */
  @media (max-width: 1280px) {
    margin: 12px 16px;
    padding: 10px 16px;
  }
`

/* 標題與 logo 一起當「回首頁」的按鈕用（導向 / → 預約管理），
   桌機與手機共用同一份樣式，游標與 hover 都在這裡定義。 */
const Title = styled.h1`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: #3e3d3c;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const MobileHeader = styled(NavBarContainer)`
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  margin: 0;
  border-radius: 0;
`

const MenuButton = styled(Button)`
  background: transparent;
  border: none;
  font-size: 20px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
  }
  .ant-drawer-header {
    padding: 16px;
    border-bottom: none;
  }
`

const StoreName = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f3d4b5 0%, #fda085 100%);
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  letter-spacing: 1px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const MobileStoreSwitchWrap = styled.div`
  margin-top: 0;
`

const MobileStoreSelect = styled(Select)`
  &.ant-select-single {
    height: auto;
  }

  .ant-select-selector {
    min-height: 40px !important;
    height: auto !important;
    padding: 6px 32px 6px 11px !important;
    display: flex !important;
    align-items: flex-start !important;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 1px solid rgba(255, 255, 255, 0.35) !important;
    color: #fff !important;
  }

  .ant-select-selection-wrap {
    align-items: flex-start !important;
  }

  .ant-select-selection-item {
    display: block !important;
    height: auto !important;
    line-height: 1.35 !important;
    white-space: normal !important;
    overflow: hidden !important;
    text-overflow: unset !important;
    color: #fff !important;
    font-weight: 600;
  }

  .ant-select-arrow {
    color: #fff !important;
  }
`

const DrawerMenu = styled(Menu)`
  border-right: none;
  .ant-menu-item {
    margin: 0;
    height: 50px;
    line-height: 50px;
  }
  .ant-menu-item-selected {
    background-color: #f0f0f0;
  }
`

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #f5f5f5;
  }
`

const UserName = styled.span`
  margin-left: 8px;
  font-weight: 500;
`

const StyledBellIcon = styled(BellOutlined)`
  font-size: 18px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    color: #ff4d8f;
  }
`

// 公告入口：桌面顯示「icon + 文字」、手機僅 icon。放在鈴鐺左側。
const AnnouncementEntry = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  cursor: pointer;
  color: #555;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    color: #7c3aed;
    background: rgba(124, 58, 237, 0.08);
  }

  .anticon {
    font-size: 18px;
  }

  .label {
    font-size: 14px;
    font-weight: 500;
  }
`

const MobileAnnouncementIcon = styled(NotificationOutlined)`
  font-size: 20px;
  color: #555;
  cursor: pointer;
  margin-right: 12px;
  transition: color 0.2s;

  &:hover {
    color: #7c3aed;
  }
`

const LogoImage = styled.img`
  height: 24px;
  margin: 0 8px 0 8px;
`

const StoreSwitchSelect = styled(Select)`
  &.ant-select-single {
    height: auto;
  }

  .ant-select-selector {
    min-height: 40px !important;
    height: auto !important;
    padding: 6px 32px 6px 11px !important;
    display: flex !important;
    align-items: flex-start !important;
  }

  .ant-select-selection-wrap {
    align-items: flex-start !important;
  }

  .ant-select-selection-item {
    display: block !important;
    height: auto !important;
    line-height: 1.35 !important;
    white-space: normal !important;
    overflow: hidden !important;
    text-overflow: unset !important;
  }
`

const NavBar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    user,
    logout,
    businessRole,
    permissions,
    businesses,
    businessId,
    switchBusiness
  } = useAuth()
  const { hasFeature, isBrandPlan, isBusinessOrBrand } = useMembership() // 會員方案權限檢查
  const { can } = usePermission()
  const canViewFinance = can('finance:view')
  const userName = user?.username || 'User'
  const avatarUrl = user?.avatar || null
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  // 公告未讀紅點：lazy 從 localStorage 讀，避免一進 NavBar 紅點先閃一下才出現
  // 網頁後台與 App 任一邊有未讀就亮 —— 判斷在 data/announcements 裡，
  // 之後再多一個平台不用回來改這裡
  const [hasUnreadAnnouncement, setHasUnreadAnnouncement] = useState(
    hasAnyUnreadAnnouncement
  )
  // 路徑變動時重抓一次（離開 /announcements 後紅點立刻消失，不必整頁重整）
  useEffect(() => {
    setHasUnreadAnnouncement(hasAnyUnreadAnnouncement())
  }, [location.pathname])
  const [visible, setVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820)
  // 線上商店待處理訂單數（待核帳 + 待出貨），顯示為選單紅點
  const [shopPendingCount, setShopPendingCount] = useState(0)
  const permissionList = Array.isArray(permissions) ? permissions : []
  const isOwner = businessRole === 'owner' || permissionList.includes('*')
  // 與 Sidebar 對齊：抽成設定僅店長以上可見
  const canManageCommission = isOwner || can('finance:manage')

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get(
          `/api/notifications/business/all/${user.businessId}`
        )
        const { notifications } = response.data
        const hasUnread = notifications.some(
          (notification) => !notification.isRead
        )
        setHasUnreadNotifications(hasUnread)
      } catch (error) {
        console.error('獲取通知失敗:', error)
      }
    }

    if (user && user.businessId) {
      fetchNotifications()
    }
  }, [user])

  useSocketMessage((notification) => {
    setHasUnreadNotifications(true)
    if (
      notification.type === 'new_booking' ||
      notification.type === 'deposit_paid' ||
      notification.type === 'booking_canceled' ||
      notification.type === 'booking_rescheduled'
    ) {
      console.log('Received booking_update message:', notification)
    }
  })

  /**
   * 定期更新商店訂單待處理數。
   * 60 秒一次即可 —— 這只是紅點提示，不需要即時，太頻繁反而增加後端負擔。
   * 條件與 Sidebar 完全一致，否則兩邊的紅點會對不起來。
   */
  useEffect(() => {
    let timer = null
    let cancelled = false

    const canSeeShop =
      hasFeature('shopOrder') && (can('shopOrder:view') || can('shopOrder:*'))
    if (!canSeeShop) {
      setShopPendingCount(0)
      return undefined
    }

    const load = async () => {
      try {
        const { getShopOrderStats } = await import('../api/shop')
        const res = await getShopOrderStats()
        if (!cancelled) setShopPendingCount(res.data?.data?.pendingAction || 0)
      } catch (e) {
        /* 徽章失敗不影響選單本身 */
      }
    }

    load()
    timer = setInterval(load, 60000)
    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.businessId])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 820)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleNotificationClick = () => {
    setHasUnreadNotifications(false)
    navigate('/notifications')
  }

  const handleAnnouncementClick = () => {
    // 點進去後 AnnouncementsPage 會把「當前分頁」的最新版本寫進 localStorage，
    // 這裡先把紅點關掉避免「點完還亮著」的視覺殘影。
    // 離開該頁時上面那個 effect 會依實際已讀狀態重算 —— 只看了一個分頁的話
    // 紅點會再亮回來，提醒店家另一邊還沒看。
    setHasUnreadAnnouncement(false)
    navigate('/announcements')
  }

  const handleSwitchBusiness = async (targetBusinessId) => {
    if (!targetBusinessId || targetBusinessId === businessId) return
    try {
      await switchBusiness(targetBusinessId)
      message.success('已切換店家')
      // 切店後強制重載，避免舊店快取殘留
      window.location.href = '/appointments'
    } catch (error) {
      console.error('切換店家失敗:', error)
      message.error(error?.response?.data?.error || error.message || '切換店家失敗')
    }
  }

  const renderStoreLabel = (name) => (
    <div style={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{name}</div>
  )

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout()
    } else {
      navigate(key)
    }
    setVisible(false) // 关闭抽屉
  }

  // 員工菜單：目前只有一個子項，直接作為單一菜單項，不使用 children
  // 如果未來需要多個子項，可以改為使用 children 結構

  const commissionChildren = canManageCommission
    ? [
        {
          key: '/commission/salary',
          label: '薪資設定'
        },
        {
          key: '/commission/monthly-salary',
          label: '每月員工薪資'
        },
        {
          key: '/commission/allowance',
          label: '零用金設定'
        },
        {
          key: '/commission/performance',
          label: '技術服務抽成設定'
        },
        {
          key: '/commission/service-rules',
          label: '服務項目抽成設定'
        },
        {
          key: '/commission/value-added',
          label: '儲值抽成設定'
        },
        {
          key: '/commission/product-sales',
          label: '產品銷售抽成設定'
        },
        {
          key: '/commission/class-ticket',
          label: '堂票銷售抽成設定'
        }
      ]
    : [
        {
          key: '/commission/monthly-salary',
          label: '我的薪資'
        },
        {
          key: '/commission/allowance',
          label: '零用金紀錄'
        }
      ]

  const commissionMenuItems =
    commissionChildren && commissionChildren.length > 0 && hasFeature('salary')
      ? [
          {
            key: '/commission',
            icon: <DollarOutlined />,
            label: isOwner ? '薪水與業績管理' : '薪資與福利',
            children: commissionChildren
          }
        ]
      : []

  const menuItems = [
    { key: '/appointments', icon: <HomeOutlined />, label: '首頁' },
    { key: '/booking-detail', icon: <CalendarOutlined />, label: '預約管理' },
    {
      key: '/customers',
      icon: <UserOutlined />,
      label: '顧客管理',
      children: [
        {
          key: '/customers',
          label: '顧客列表'
        },
        ...(hasFeature('customMembership')
          ? [
              {
                key: '/customers/membership-tiers',
                icon: <CrownOutlined />,
                label: '會員等級設置'
              },
              {
                key: '/customers/groups',
                icon: <TeamOutlined />,
                label: '顧客群組'
              }
            ]
          : []),
        ...(hasFeature('revisitReminder')
          ? [
              {
                key: '/customers/revisit-reminders',
                icon: <BellOutlined />,
                label: '回訪提醒'
              }
            ]
          : [])
      ]
    },
    // 只有有 finance:view 权限才显示数据报表菜单
    ...(canViewFinance
      ? [
          {
            key: '/dashboard',
            icon: <DashboardOutlined />,
            label: '數據報表',
            children: [
              {
                key: '/dashboard',
                label: '數據報表'
              },
              ...(hasFeature('checkout')
                ? [
                    {
                      key: '/dashboard/revenue-records',
                      label: '營收紀錄查詢'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    // 顧客評價：全方案開放，但要有 review:view（設計師以上預設都有）
    ...(hasFeature('review') &&
    (can('review:view') || can('review:manage') || can('review:reply_own'))
      ? [{ key: '/reviews', icon: <StarOutlined />, label: '顧客評價' }]
      : []),
    { key: '/coupons', icon: <RiCoupon3Line />, label: '優惠模組' },
    { key: '/products', icon: <AppstoreOutlined />, label: '服務' },
    { key: '/employees', icon: <TeamOutlined />, label: '員工' },
    ...(hasFeature('schedule') && can('schedule:view')
      ? [{ key: '/schedule', icon: <ScheduleOutlined />, label: '班表' }]
      : []),
    ...(hasFeature('recruitment')
      ? [
          {
            key: '/recruitment-dashboard',
            icon: <UserAddOutlined />,
            label: '招聘管理'
          }
        ]
      : []),
    ...commissionMenuItems,
    ...(hasFeature('storedValue')
      ? [
          {
            key: '/value-added',
            icon: <WalletOutlined />,
            label: '儲值管理',
            children: [
              {
                key: '/value-added/purchase',
                label: '儲值購買'
              },
              {
                key: '/value-added/remaining-balance',
                label: '剩餘儲值金查詢'
              },
              {
                key: '/value-added/records',
                label: '儲值記錄查詢'
              },
              {
                key: '/value-added/consumption',
                label: '儲值消費紀錄'
              }
            ]
          }
        ]
      : []),
    ...(hasFeature('classTicket')
      ? [
          {
            key: '/class-ticket',
            icon: <IdcardOutlined />,
            label: '堂票模組',
            children: [
              {
                key: '/class-ticket/settings',
                label: '堂票設定'
              },
              {
                key: '/class-ticket/records',
                label: '堂票紀錄列表'
              }
            ]
          }
        ]
      : []),
    ...(hasFeature('consentForm')
      ? [
          {
            key: '/consent-form',
            icon: <FileProtectOutlined />,
            label: '同意書模組',
            children: [
              {
                key: '/consent-form/settings',
                label: '同意書設定'
              },
              {
                key: '/consent-form/records',
                label: '簽署紀錄'
              }
            ]
          }
        ]
      : []),
    // 結帳管理
    ...(hasFeature('checkout')
      ? [
          {
            key: '/checkout',
            icon: <CheckCircleOutlined />,
            label: '結帳管理',
            children: [
              {
                key: '/checkout/checkout',
                label: '結帳'
              },
              {
                key: '/checkout/records',
                label: '結帳記錄查詢'
              },
              ...(hasFeature('installment') &&
              (can('installment:view') || can('installment:manage'))
                ? [
                    {
                      key: '/checkout/installments',
                      label: '分期收款'
                    }
                  ]
                : []),
              {
                key: '/checkout/employee-order',
                label: '員工自購或領料打單'
              },
              {
                key: '/checkout/employee-records',
                label: '員工自購或領料記錄'
              }
            ]
          }
        ]
      : []),
    // 庫存管理：方案要開通 + 至少有一個子頁權限才顯示
    ...(hasFeature('inventory') &&
    (can('inventory:view') || can('inventory:manage') || can('inventory:*'))
      ? [
          {
            key: '/inventory',
            icon: <ShoppingCartOutlined />,
            label: '庫存管理',
            children: [
              // 新增/採購、進貨管理：需要 inventory:manage
              ...(can('inventory:manage') || can('inventory:*')
                ? [
                    {
                      key: '/inventory/products/add',
                      label: '新增/採購商品'
                    },
                    {
                      key: '/inventory/purchase',
                      label: '進貨管理'
                    }
                  ]
                : []),
              // 進貨紀錄、商品管理、使用記錄、拆公用：需要 inventory:view 或以上
              // 拆公用允許員工執行（成本欄位會在頁內依 inventory:manage 控制是否可編輯）
              ...(can('inventory:view') ||
              can('inventory:manage') ||
              can('inventory:*')
                ? [
                    {
                      key: '/inventory/purchase-records',
                      label: '進貨紀錄'
                    },
                    {
                      key: '/inventory/products',
                      label: '商品管理'
                    },
                    {
                      key: '/inventory/usage',
                      label: '使用記錄'
                    },
                    {
                      key: '/inventory/public-use',
                      label: '拆公用'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    // 線上商店：方案要開通 + 至少有一個子頁權限才顯示整個 menu。
    // 條件與 Sidebar 一字不差，兩邊看到的東西必須一樣。
    ...(hasFeature('shopOrder') &&
    (can('shopOrder:view') ||
      can('shopOrder:manage') ||
      can('shopSetting:manage') ||
      can('shopOrder:*'))
      ? [
          {
            key: '/shop',
            icon: <ShopOutlined />,
            label:
              shopPendingCount > 0
                ? (
                  <span>
                    線上商店
                    <ShopBadge>
                      {shopPendingCount > 99 ? '99+' : shopPendingCount}
                    </ShopBadge>
                  </span>
                  )
                : (
                    '線上商店'
                  ),
            children: [
              // 訂單：只要有 shopOrder:view 就看得到（會計對帳、設計師查詢）
              ...(can('shopOrder:view') || can('shopOrder:*')
                ? [
                    {
                      key: '/shop/orders',
                      label:
                        shopPendingCount > 0
                          ? (
                            <span>
                              商店訂單
                              <ShopBadge>
                                {shopPendingCount > 99
                                  ? '99+'
                                  : shopPendingCount}
                              </ShopBadge>
                            </span>
                            )
                          : (
                              '商店訂單'
                            )
                    }
                  ]
                : []),
              // 取貨核銷：櫃檯用，需要改狀態的權限
              ...(can('shopOrder:manage') || can('shopOrder:*')
                ? [{ key: '/shop/pickup', label: '取貨核銷' }]
                : []),
              // 上架與設定：限 shopSetting:manage（櫃檯只處理訂單，不改設定）
              ...(can('shopSetting:manage') || can('shopOrder:*')
                ? [
                    { key: '/shop/products', label: '上架商品' },
                    { key: '/shop/settings', label: '商店設定' }
                  ]
                : [])
            ]
          }
        ]
      : []),
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '設定',
      children: [
        {
          key: '/settings/info',
          icon: <HomeOutlined />,
          label: '店家資訊'
        },
        {
          key: '/settings/hours',
          icon: <ClockCircleOutlined />,
          label: '營業時間'
        },
        {
          key: '/settings/daysOff',
          icon: <CalendarOutlined />,
          label: '休假日設定'
        },
        {
          key: '/settings/appointment',
          icon: <SettingOutlined />,
          label: '預約設定'
        },
        {
          key: '/settings/deposit',
          icon: <FaMoneyBillWave />,
          label: '定金設定'
        },
        {
          key: '/settings/booking',
          icon: <RiNotificationBadgeFill />,
          label: '預約須知'
        },
        {
          key: '/settings/cancel',
          icon: <TbCalendarCancel />,
          label: '取消規定'
        },
        {
          key: '/settings/line',
          icon: <MessageOutlined />,
          label: 'LINE 整合'
        },
        {
          key: '/settings/myLineNotify',
          icon: <MessageOutlined />,
          label: '我的 LINE 通知'
        },
        ...(isBusinessOrBrand()
          ? [
              {
                key: '/settings/calendar',
                icon: <GoogleOutlined />,
                label: 'Google 日曆同步'
              }
            ]
          : []),
        {
          key: '/settings/membership',
          icon: <CrownOutlined />,
          label: '會員資訊'
        },
        {
          key: '/settings/payment',
          icon: <CreditCardOutlined />,
          label: '金流設定'
        },
        {
          key: '/settings/password',
          icon: <LockOutlined />,
          label: '修改密碼'
        }
      ]
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '登出',
      danger: true
    }
  ]

  const renderMobileNav = () => (
    <>
      <MobileHeader>
        <MenuButton
          data-tour='mobile-menu'
          icon={<MenuOutlined />}
          onClick={() => setVisible(true)}
        />
        <Title
          onClick={() => navigate('/')}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') navigate('/')
          }}
          title='回到首頁'
        >
          美感 | BeauteFeel
        </Title>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Badge dot={hasUnreadAnnouncement} offset={[-2, 4]}>
            <MobileAnnouncementIcon
              onClick={handleAnnouncementClick}
              aria-label='更新公告'
            />
          </Badge>
          <Badge dot={hasUnreadNotifications}>
            <BellOutlined
              style={{ fontSize: '20px', cursor: 'pointer' }}
              onClick={handleNotificationClick}
            />
          </Badge>
        </div>
      </MobileHeader>
      <StyledDrawer
        title={
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            <LogoImage src='/saa.png' alt='BeauteFeel' />
            <div>美感 | 店家管理</div>
          </div>
        }
        placement='left'
        onClose={() => setVisible(false)}
        open={visible}
        width={250}
      >
        {Array.isArray(businesses) && businesses.length > 1 && (
          <StoreName>
            <MobileStoreSwitchWrap>
              <MobileStoreSelect
                style={{ width: '100%' }}
                value={businessId || undefined}
                onChange={handleSwitchBusiness}
                options={businesses.map((b) => ({
                  label: renderStoreLabel(b.businessName),
                  value: b.businessId
                }))}
              />
            </MobileStoreSwitchWrap>
          </StoreName>
        )}
        {(!Array.isArray(businesses) || businesses.length <= 1) && (
          <StoreName>{user?.name || '店家管理'}</StoreName>
        )}
        <DrawerMenu
          mode='inline'
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </StyledDrawer>
    </>
  )

  const renderDesktopNav = () => (
    <NavBarContainer>
      <Title
        onClick={() => navigate('/')}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') navigate('/')
        }}
        title='回到首頁'
      >
        <LogoImage src='/saa.png' alt='BeauteFeel' />
        美感 | BeauteFeel
      </Title>
      <RightContainer>
        {Array.isArray(businesses) && businesses.length > 1 && (
          <StoreSwitchSelect
            value={businessId || undefined}
            onChange={handleSwitchBusiness}
            style={{ width: 360 }}
            options={businesses.map((b) => ({
              label: renderStoreLabel(b.businessName),
              value: b.businessId
            }))}
          />
        )}
        <Badge dot={hasUnreadAnnouncement} offset={[-2, 4]}>
          <AnnouncementEntry
            onClick={handleAnnouncementClick}
            title='查看系統更新公告'
          >
            <NotificationOutlined />
            <span className='label'>更新公告</span>
          </AnnouncementEntry>
        </Badge>
        <Badge dot={hasUnreadNotifications}>
          <StyledBellIcon onClick={handleNotificationClick} />
        </Badge>
        <Dropdown
          menu={{
            items: menuItems,
            onClick: handleMenuClick
          }}
          trigger={['click']}
        >
          <AvatarContainer>
            <Avatar src={`${PHOTO_URL}${avatarUrl}`} />
            <UserName>{userName}</UserName>
            <DownOutlined style={{ marginLeft: '8px' }} />
          </AvatarContainer>
        </Dropdown>
      </RightContainer>
    </NavBarContainer>
  )

  return isMobile ? renderMobileNav() : renderDesktopNav()
}

export default NavBar
